export interface ThumbnailOptions {
  width: number; // Default: 320
  height: number; // Default: 80
  isClinicalMode: boolean;
  montageName: string;
  timestamp: Date;
}

export interface ThumbnailResult {
  dataUrl: string;
  width: number;
  height: number;
  generatedAt: Date;
}

export class PerspectiveThumbnailService {
  // Cache for reusable canvas elements (performance optimization)
  private static canvasPool: HTMLCanvasElement[] = [];
  private static readonly MAX_POOL_SIZE = 5;

  /**
   * Capture canvas snapshot and generate thumbnail
   * @param sourceCanvas - The EEG waveform canvas
   * @param options - Thumbnail generation options
   * @returns Data URL of thumbnail
   */
  static async generateThumbnail(
    sourceCanvas: HTMLCanvasElement,
    options: ThumbnailOptions
  ): Promise<ThumbnailResult> {
    const { width, height, isClinicalMode, montageName, timestamp } = options;

    // Get canvas from pool or create new
    const thumbCanvas = this.getCanvasFromPool(width, height);
    const ctx = thumbCanvas.getContext('2d', {
      alpha: true,
      desynchronized: true, // Performance hint for better rendering
    });

    if (!ctx) {
      throw new Error('Failed to get canvas context for thumbnail generation');
    }

    // Use requestAnimationFrame for smooth rendering
    return new Promise<ThumbnailResult>((resolve) => {
      requestAnimationFrame(() => {
        // Draw scaled source canvas
        ctx.drawImage(sourceCanvas, 0, 0, width, height);

        // Add watermark overlay
        this.addWatermark(
          ctx,
          width,
          height,
          isClinicalMode,
          montageName,
          timestamp
        );

        // Compress to data URL
        const dataUrl = this.compressThumbnail(thumbCanvas, 0.8);

        // Return canvas to pool
        this.returnCanvasToPool(thumbCanvas);

        resolve({
          dataUrl,
          width,
          height,
          generatedAt: new Date(),
        });
      });
    });
  }

  /**
   * Generate multiple thumbnails in batch (optimized for performance)
   * @param jobs - Array of thumbnail generation jobs
   * @returns Array of thumbnail results
   */
  static async generateThumbnailBatch(
    jobs: Array<{
      sourceCanvas: HTMLCanvasElement;
      options: ThumbnailOptions;
    }>
  ): Promise<ThumbnailResult[]> {
    // Process in chunks to avoid blocking the main thread
    const chunkSize = 3;
    const results: ThumbnailResult[] = [];

    for (let i = 0; i < jobs.length; i += chunkSize) {
      const chunk = jobs.slice(i, i + chunkSize);
      const chunkResults = await Promise.all(
        chunk.map(job => this.generateThumbnail(job.sourceCanvas, job.options))
      );
      results.push(...chunkResults);

      // Yield to browser between chunks
      if (i + chunkSize < jobs.length) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    return results;
  }

  /**
   * Get canvas from pool or create new one
   * @private
   */
  private static getCanvasFromPool(width: number, height: number): HTMLCanvasElement {
    const canvas = this.canvasPool.pop();

    if (canvas) {
      canvas.width = width;
      canvas.height = height;
      return canvas;
    }

    const newCanvas = document.createElement('canvas');
    newCanvas.width = width;
    newCanvas.height = height;
    return newCanvas;
  }

  /**
   * Return canvas to pool for reuse
   * @private
   */
  private static returnCanvasToPool(canvas: HTMLCanvasElement): void {
    if (this.canvasPool.length < this.MAX_POOL_SIZE) {
      // Clear canvas before returning
      const ctx = canvas.getContext('2d');
      if (ctx && typeof ctx.clearRect === 'function') {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      this.canvasPool.push(canvas);
    }
  }

  /**
   * Clear canvas pool (call when no longer needed)
   */
  static clearCanvasPool(): void {
    this.canvasPool = [];
  }

  /**
   * Add watermark overlay to thumbnail
   * @param ctx - Canvas 2D context
   * @param isClinicalMode - Whether in clinical mode
   * @param montageName - Name of montage
   * @param timestamp - Timestamp for capture
   */
  private static addWatermark(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    isClinicalMode: boolean,
    montageName: string,
    timestamp: Date
  ): void {
    // Semi-transparent footer overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, height - 24, width, 24);

    // Montage name + timestamp
    ctx.fillStyle = '#fff';
    ctx.font = '11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(
      `${montageName} • ${timestamp.toLocaleTimeString()}`,
      4,
      height - 8
    );

    // Clinical mode watermark at top
    if (isClinicalMode) {
      ctx.fillStyle = 'rgba(211, 47, 47, 0.9)';
      ctx.fillRect(0, 0, width, 20);

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Preview — Not for Diagnosis', width / 2, 14);
    }
  }

  /**
   * Compress thumbnail to data URL
   * @param canvas - Canvas element
   * @param quality - JPEG quality (0-1)
   * @returns Data URL string
   */
  private static compressThumbnail(
    canvas: HTMLCanvasElement,
    quality: number
  ): string {
    return canvas.toDataURL('image/jpeg', quality);
  }
}

export default PerspectiveThumbnailService;
